/** Manages the Marked passive: tags enemies who lose sight of the player, renders indicator rings, slows aggro decay. */
import Phaser from 'phaser';
import type { Enemy } from '../entities/Enemy';
import { ensureRingTexture } from '../ui/CrispUtil';

const CRISP_SCALE = 2;
const INV_CRISP = 1 / CRISP_SCALE;
const RING_RADIUS = 12;
const RING_LINE_WIDTH = 1.5;
const RING_COLOR = 0xff6644;
const RING_ALPHA = 0.6;

interface MarkEntry {
  enemy: Enemy;
  timer: number;
  ring: Phaser.GameObjects.Image;
}

/**
 * Tracks enemies tagged by the Marked passive: when an enemy transitions from
 * chase/using_skill to search/alert/duty, it gets marked for a duration.
 * Marked enemies have a visible ring indicator and slower aggro decay.
 */
export class MarkRenderer {
  private scene: Phaser.Scene;
  private enabled = false;
  private markDurationMs = 8000;
  private aggroDecayReduction = 0.3;
  private marks: MarkEntry[] = [];
  private ringTextureKey: string | null = null;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  /** Enable the Marked system with config values from the Specter tree. */
  enable(markDurationMs: number, aggroDecayReduction: number): void {
    this.enabled = true;
    this.markDurationMs = markDurationMs;
    this.aggroDecayReduction = aggroDecayReduction;
  }

  /** Disable the Marked system and clear all active marks. */
  disable(): void {
    this.enabled = false;
    this.clearAll();
  }

  /**
   * Check enemies for mark-triggering transitions and update active marks.
   * Call once per frame after enemy AI updates.
   */
  update(enemies: readonly Enemy[], delta: number): void {
    if (!this.enabled) return;

    // Check for new marks
    for (const enemy of enemies) {
      if (enemy.ai.justLostPlayer && !this.isMarked(enemy)) {
        this.addMark(enemy);
      }
    }

    // Update existing marks
    for (let i = this.marks.length - 1; i >= 0; i--) {
      const mark = this.marks[i];
      mark.timer -= delta;

      if (mark.timer <= 0 || mark.enemy.healthComponent.isDead) {
        this.removeMark(i);
        continue;
      }

      // Position ring on enemy
      mark.ring.setPosition(mark.enemy.sprite.x, mark.enemy.sprite.y);
      mark.ring.setDepth(mark.enemy.sprite.depth - 1);
      mark.ring.setVisible(true);

      // Pulse alpha in the last 2 seconds
      if (mark.timer < 2000) {
        const pulse = 0.3 + 0.3 * Math.sin(mark.timer * 0.01);
        mark.ring.setAlpha(pulse);
      }
    }
  }

  /** Check if an enemy is already marked. */
  private isMarked(enemy: Enemy): boolean {
    return this.marks.some(m => m.enemy === enemy);
  }

  /** Tag an enemy with a mark ring and slow its aggro decay. */
  private addMark(enemy: Enemy): void {
    if (!this.ringTextureKey) {
      this.ringTextureKey = ensureRingTexture(this.scene, RING_RADIUS * CRISP_SCALE, RING_LINE_WIDTH * CRISP_SCALE);
    }

    const ring = this.scene.add.image(enemy.sprite.x, enemy.sprite.y, this.ringTextureKey);
    ring.setScale(INV_CRISP);
    ring.setTint(RING_COLOR);
    ring.setAlpha(RING_ALPHA);
    ring.setDepth(enemy.sprite.depth - 1);

    // Slow aggro decay (reduction of 0.3 means decay is 30% slower → multiplier = 1 / (1 - 0.3) ≈ 1.43)
    enemy.ai.aggroDecayMultiplier = 1 / (1 - this.aggroDecayReduction);

    this.marks.push({ enemy, timer: this.markDurationMs, ring });
  }

  /** Remove a mark by index, restoring the enemy's aggro decay rate. */
  private removeMark(index: number): void {
    const mark = this.marks[index];
    mark.ring.destroy();
    // Restore normal decay rate (only if enemy is still alive)
    if (!mark.enemy.healthComponent.isDead) {
      mark.enemy.ai.aggroDecayMultiplier = 1.0;
    }
    this.marks.splice(index, 1);
  }

  /** Remove all active marks. */
  private clearAll(): void {
    for (const mark of this.marks) {
      mark.ring.destroy();
      if (!mark.enemy.healthComponent.isDead) {
        mark.enemy.ai.aggroDecayMultiplier = 1.0;
      }
    }
    this.marks.length = 0;
  }

  /** Clean up all rings. */
  destroy(): void {
    this.clearAll();
  }
}
